import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BookOpen, FileSearch, Loader2, PenLine, Quote, X } from 'lucide-react';
import type { HolderChoice } from '../../types/activationReadiness';
import HoldersForm, { holderDrafts } from './HoldersForm';
import { StatusChip, type StatusTone } from '../../components/StatusChip';
import { Button, Card, CardHead, Eyebrow, INK } from './reviewStyles';
import { KIND_LABEL, PROBLEM_LABEL, type ReviewRow, formatStamp } from './reviewModel';

const Big = styled.div`
  margin-top: 0.25rem;
  color: ${INK.strong};
  font-size: 1.05rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const Detail = styled.div`
  margin-top: 0.2rem;
  color: ${INK.muted};
  font-size: 0.72rem;
  line-height: 1.45;
`;

const Block = styled.div`
  margin-top: 0.8rem;
  padding: 0.7rem 0.75rem;
  border: 1px solid ${INK.line};
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.5);
`;

const Line = styled.div`
  display: flex;
  gap: 0.5rem;
  color: ${INK.soft};
  font-size: 0.74rem;
  line-height: 1.5;
  & + & { margin-top: 0.3rem; }
  svg { flex: 0 0 auto; margin-top: 0.15rem; color: ${INK.accentSoft}; }
`;

const Excerpt = styled.blockquote`
  margin: 0.5rem 0 0;
  padding: 0.45rem 0.6rem;
  border-left: 3px solid ${INK.accent};
  border-radius: 4px;
  background: rgba(12, 74, 110, 0.16);
  color: ${INK.body};
  font-family: ui-serif, Georgia, 'Times New Roman', serif;
  font-size: 0.8rem;
  line-height: 1.5;
`;

const Viewer = styled.div`
  position: relative;
  margin-top: 0.7rem;
  overflow: hidden;
  border: 1px solid ${INK.lineStrong};
  border-radius: 10px;
  background: #0b1220;
  iframe { display: block; width: 100%; height: min(70vh, 44rem); border: 0; background: #fff; }
`;

const ViewerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem 0.4rem 0.7rem;
  border-bottom: 1px solid ${INK.line};
  color: ${INK.soft};
  font-size: 0.68rem;
`;

const DocumentDialog = styled.dialog`
  /* Centre the modal ourselves: the CSS reset zeroes every margin, including the auto margin a
     browser uses to centre a modal dialog, which left it in the top-left corner. */
  margin: auto;
  width: calc(100vw - 64px); max-width: 1600px; height: calc(100vh - 64px);
  padding: 0; background: #0b1220; color: #e5edf7; border: 1px solid #43617f; border-radius: 10px;
  &::backdrop { background: #020617cc; }
  iframe { width: 100%; height: calc(100% - 52px); border: 0; background: white; }
`;

export interface EvidencePanelProps {
  row: ReviewRow | null;
  documentName: string | null;
  canShowDocument: boolean;
  loadDocument: () => Promise<string>;
  versionId: number;
  /** Replaces the status the row's review state would show, for surfaces with their own states. */
  chip?: { tone: StatusTone; label: string };
  /** Shown under the citation, for what the surface knows about the source beyond page and clause. */
  sourceNote?: React.ReactNode;
  /** Controls for the citation, placed between it and the document. */
  citationControls?: React.ReactNode;
  /** What the source document is called on this surface; an agreement unless said otherwise. */
  documentNoun?: string;
  /** Offered only while the version is still open and the viewer may prepare it. */
  fundChange?: {
    options: { id: number; portfolio_code: string; portfolio_name: string }[];
    holders?: HolderChoice[] | null;
    portfolioId?: number | null;
    onChanged: (message: string) => Promise<void>;
  };
}

/** The selected item's value, its source in the agreement, and the agreement page itself. */
const EvidencePanel: React.FC<EvidencePanelProps> = ({
  row, documentName, canShowDocument, loadDocument, versionId, fundChange, chip, sourceNote, citationControls, documentNoun = 'agreement',
}) => {
  const documentDialog = useRef<HTMLDialogElement>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const page = row?.citation?.page ? Number.parseInt(row.citation.page, 10) : NaN;

  useEffect(() => { setError(null); }, [row?.uid]);

  const openPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = viewerUrl || await loadDocument();
      setViewerUrl(url);
      setViewerOpen(true);
    } catch {
      setError(`The ${documentNoun} PDF could not be loaded.`);
    } finally {
      setLoading(false);
    }
  };

  if (!row) {
    return (
      <Card>
        <CardHead><div><h2>Source</h2><p>Select any item to see where it comes from in the {documentNoun}.</p></div><FileSearch size={18} color={INK.accent} /></CardHead>
      </Card>
    );
  }

  const tone = row.status === 'OPEN' ? 'warning' : row.status === 'DONE' ? 'done' : row.status === 'REJECTED' ? 'critical' : 'neutral';
  const label = row.status === 'OPEN' ? (PROBLEM_LABEL[row.problem || ''] || 'Needs review')
    : row.status === 'DONE' ? 'Confirmed' : row.status === 'REJECTED' ? 'Rejected' : 'Informational';
  const src = viewerUrl && Number.isFinite(page) ? `${viewerUrl}#page=${page}&view=FitH` : viewerUrl || '';

  return (
    <Card aria-live="polite">
      <CardHead>
        <div>
          <Eyebrow>{KIND_LABEL[row.kind]}{row.kind === 'TERM' ? ` · ${row.group}` : ''}</Eyebrow>
          <h2 style={{ marginTop: 4 }}>{row.label}</h2>
        </div>
        <StatusChip tone={chip?.tone || tone} label={chip?.label || label} />
      </CardHead>
      <Big>{row.value}</Big>
      {row.detail && <Detail>{row.detail}</Detail>}
      {row.corrected && row.extractedValue && <Detail>Read from the PDF as {row.extractedValue}; corrected by the reviewer.</Detail>}
      {row.kind === 'TERM' && !row.required && !row.material && <Detail>Informational. Not required for activation.</Detail>}

      <Block>
        {row.entered ? (
          // F-MK-084: an entry with no source document is said to be keyed, not shown an empty viewer.
          row.entered.source ? <>
            <Line><PenLine size={13} aria-hidden="true" /><span>Entered by {row.entered.by}{row.entered.at ? `, ${formatStamp(row.entered.at)}` : ''}</span></Line>
            <Line><BookOpen size={13} aria-hidden="true" /><span>From {row.entered.source}</span></Line>
          </> : <Line><PenLine size={13} aria-hidden="true" /><span>Keyed by {row.entered.by}{row.entered.at ? `, ${formatStamp(row.entered.at)}` : ''}, no document</span></Line>
        ) : null}
        {row.citation ? (
          <>
            <Line><BookOpen size={13} aria-hidden="true" /><span>{row.citation.page ? `Page ${row.citation.page}` : 'Page not recorded'}{row.citation.section ? ` · ${row.citation.section}` : ''}</span></Line>
            {row.citation.clause && row.citation.clause !== row.citation.section && <Line><Quote size={13} aria-hidden="true" /><span>{row.citation.clause}</span></Line>}
            {row.citation.excerpt && <Excerpt>“{row.citation.excerpt}”</Excerpt>}
          </>
        ) : !row.entered ? (
          <Line style={{ color: '#fcd34d' }}><BookOpen size={13} aria-hidden="true" /><span>No source in the {documentNoun} yet.</span></Line>
        ) : null}
        {sourceNote && <Detail style={{ marginTop: 6 }}>{sourceNote}</Detail>}
        {row.confidence != null && <Detail style={{ marginTop: 6 }}>Extraction confidence {Math.round(Number(row.confidence) <= 1 ? Number(row.confidence) * 100 : Number(row.confidence))}%</Detail>}
      </Block>

      {citationControls}

      {row.kind === 'FUND' && fundChange && (
        <div style={{ marginTop: 10 }}>
          {!changing ? (
            <Button type="button" onClick={() => setChanging(true)}><PenLine size={13} /> Change funds</Button>
          ) : (
            <HoldersForm versionId={versionId} options={fundChange.options} initial={holderDrafts(fundChange.holders, fundChange.portfolioId)}
              idPrefix="fund-change" submitLabel="Save funds"
              onSaved={async message => { setChanging(false); await fundChange.onChanged(message); }}
              onCancel={() => setChanging(false)} />
          )}
        </div>
      )}

      {canShowDocument && (
        <div style={{ marginTop: 10 }}>
          {!viewerOpen ? (
            <Button type="button" onClick={openPage} disabled={loading}>
              {loading ? <Loader2 size={13} className="spin" /> : <FileSearch size={13} />}
              {Number.isFinite(page) ? `Show page ${page} of the ${documentNoun}` : `Show the ${documentNoun}`}
            </Button>
          ) : (
            <Viewer>
              <ViewerBar>
                <span>{documentName || 'Agreement'}{Number.isFinite(page) ? ` · page ${page}` : ''}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="button" onClick={() => documentDialog.current?.showModal()}>Expand document</Button>
                  <Button type="button" $variant="quiet" onClick={() => setViewerOpen(false)} aria-label="Close the agreement"><X size={13} /></Button>
                </div>
              </ViewerBar>
              <DocumentDialog ref={documentDialog} aria-label="Expanded agreement">
                <ViewerBar><span>{documentName || 'Agreement'}</span>
                  <Button type="button" onClick={() => documentDialog.current?.close()} autoFocus>Close document</Button>
                </ViewerBar>
                <iframe title="Expanded agreement PDF" src={src} />
              </DocumentDialog>
              <iframe key={src} title={`Agreement${Number.isFinite(page) ? `, page ${page}` : ''}`} src={src} />
            </Viewer>
          )}
          {error && <Detail style={{ color: '#fca5a5' }}>{error}</Detail>}
        </div>
      )}
    </Card>
  );
};

export default EvidencePanel;
